import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { problemMessage, statusOf } from '../api/baseApi'

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 3, color: 'text.secondary' }}>
      <CircularProgress size={20} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  )
}

/**
 * Render a failed request.
 *
 * Both surfaces answer RFC 9457, so one renderer covers every failure —
 * a 400 from the model and a 403 from the membership check reach here in
 * the same shape. The status is named because 403 and 404 mean different
 * things to an author and the `detail` alone does not always say which.
 */
export function QueryError({ error, title }: { error: unknown; title?: string }) {
  const status = statusOf(error)
  return (
    <Box sx={{ p: 3 }}>
      <Alert severity={status === 403 ? 'warning' : 'error'}>
        <AlertTitle>{title ?? (status ? `Something went wrong (${status})` : 'Something went wrong')}</AlertTitle>
        {problemMessage(error)}
      </Alert>
    </Box>
  )
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Paper sx={{ p: 4, textAlign: 'center' }}>
      <Stack spacing={1.5} alignItems="center">
        <Typography variant="h3">{title}</Typography>
        {children}
      </Stack>
    </Paper>
  )
}
